import React from 'react';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { theme } from '../../styles/theme';
import { PUSH_SCREENS, PUSH_SCREEN_GROUPS } from '../../config/pushScreens';
import { DEFAULT_CHANNEL_ID, PUSH_CHANNELS, SILENT_CHANNEL_ID, TTL_OPTIONS, findPushChannel } from '../../config/pushChannels';
import { customDataError } from '../../utils/pushOptions';
import type { PushOptionsValue } from '../../types/push';

interface Props {
  value: PushOptionsValue;
  onChange: (value: PushOptionsValue) => void;
  disabled?: boolean;
}

const versionTag = (minAppVersion?: string) => (minAppVersion ? ` · app ${minAppVersion.replace(/\.0$/, '')}+` : '');

/** Opções do envio, no espírito do "Outras opções" do console do Firebase. */
const PushOptions: React.FC<Props> = ({ value, onChange, disabled }) => {
  const update = (changes: Partial<PushOptionsValue>) => onChange({ ...value, ...changes });

  // No Android 8+ quem decide o som é o canal: som e canal andam juntos.
  const setSound = (sound: boolean) =>
    update({
      sound,
      androidChannelId: sound
        ? (value.androidChannelId === SILENT_CHANNEL_ID ? DEFAULT_CHANNEL_ID : value.androidChannelId)
        : SILENT_CHANNEL_ID,
    });

  const setChannel = (androidChannelId: string) =>
    update({ androidChannelId, sound: findPushChannel(androidChannelId)?.sound ?? value.sound });

  const setRow = (index: number, changes: Partial<{ key: string; value: string }>) =>
    update({ customData: value.customData.map((row, i) => (i === index ? { ...row, ...changes } : row)) });

  const dataError = customDataError(value.customData);

  return (
    <div>
      <div style={styles.formGroup}>
        <label style={styles.label}>
          Nome interno <span style={styles.hint}>- Opcional, aparece só no histórico (padrão: o título)</span>
        </label>
        <input
          style={styles.input}
          type="text"
          value={value.name}
          onChange={(e) => update({ name: e.target.value })}
          placeholder="Ex: Lançamento do Financeiro v2"
          maxLength={100}
          disabled={disabled}
        />
      </div>

      <div style={styles.grid}>
        <div>
          <label style={styles.label}>Tela de destino</label>
          <select
            style={styles.select}
            value={value.screen}
            onChange={(e) => update({ screen: e.target.value })}
            disabled={disabled}
          >
            <option value="">Nenhuma — só abre o app</option>
            {PUSH_SCREEN_GROUPS.map(group => (
              <optgroup key={group} label={group}>
                {PUSH_SCREENS.filter(screen => screen.group === group).map(screen => (
                  <option key={screen.value} value={screen.value}>
                    {screen.label}{versionTag(screen.minAppVersion)}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>

        <div>
          <label style={styles.label}>Validade</label>
          <select
            style={styles.select}
            value={value.ttlSeconds}
            onChange={(e) => update({ ttlSeconds: Number(e.target.value) })}
            disabled={disabled}
            title="Se o aparelho ficar offline mais tempo que isso, a notificação é descartada"
          >
            {TTL_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={styles.label}>Som</label>
          <label style={styles.toggle}>
            <input
              type="checkbox"
              checked={value.sound}
              onChange={(e) => setSound(e.target.checked)}
              disabled={disabled}
              style={styles.checkbox}
            />
            {value.sound ? 'Ligado' : 'Desligado'}
          </label>
        </div>

        <div>
          <label style={styles.label}>Canal Android</label>
          <select
            style={styles.select}
            value={value.androidChannelId}
            onChange={(e) => setChannel(e.target.value)}
            disabled={disabled}
          >
            {PUSH_CHANNELS.map(channel => (
              <option key={channel.id} value={channel.id}>
                {channel.label}{versionTag(channel.minAppVersion)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {!value.sound && (
        <div style={styles.notice}>
          Em versões do app anteriores à 1.12, no Android a notificação ainda pode tocar som.
        </div>
      )}

      <div style={styles.formGroup}>
        <label style={styles.label}>
          Dados personalizados <span style={styles.hint}>- Opcional, chegam no app junto com a notificação</span>
        </label>
        {value.customData.map((row, index) => (
          <div key={index} style={styles.dataRow}>
            <input
              style={styles.input}
              type="text"
              value={row.key}
              onChange={(e) => setRow(index, { key: e.target.value })}
              placeholder="Chave"
              disabled={disabled}
            />
            <input
              style={styles.input}
              type="text"
              value={row.value}
              onChange={(e) => setRow(index, { value: e.target.value })}
              placeholder="Valor"
              disabled={disabled}
            />
            <button
              type="button"
              style={styles.removeButton}
              onClick={() => update({ customData: value.customData.filter((_, i) => i !== index) })}
              disabled={disabled}
              title="Remover"
            >
              <CloseIcon style={{ fontSize: 18 }} />
            </button>
          </div>
        ))}
        <button
          type="button"
          style={styles.addButton}
          onClick={() => update({ customData: [...value.customData, { key: '', value: '' }] })}
          disabled={disabled}
        >
          <AddIcon style={{ fontSize: 18, marginRight: 4 }} />
          Adicionar dado
        </button>
        {dataError && <div style={styles.error}>{dataError}</div>}
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  formGroup: {
    marginBottom: '20px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '20px',
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: 'bold',
    color: theme.colors.text,
    marginBottom: '8px',
  },
  hint: {
    color: '#999',
    fontSize: '12px',
    fontWeight: 'normal',
  },
  input: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    outline: 'none',
  },
  select: {
    width: '100%',
    padding: '12px',
    fontSize: '15px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    outline: 'none',
    background: 'white',
    cursor: 'pointer',
  },
  toggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 0',
    fontSize: '15px',
    color: theme.colors.text,
    cursor: 'pointer',
  },
  checkbox: {
    width: '20px',
    height: '20px',
    cursor: 'pointer',
  },
  notice: {
    marginTop: '-8px',
    marginBottom: '20px',
    padding: '10px 14px',
    background: '#fff8e1',
    color: '#8a5300',
    borderRadius: '8px',
    fontSize: '13px',
  },
  dataRow: {
    display: 'flex',
    gap: '10px',
    marginBottom: '10px',
  },
  removeButton: {
    flexShrink: 0,
    width: '44px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    background: 'white',
    color: '#999',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  addButton: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '8px 14px',
    borderRadius: '8px',
    border: `2px dashed ${theme.colors.primary}`,
    background: 'white',
    color: theme.colors.primary,
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
  },
  error: {
    marginTop: '8px',
    color: '#d32f2f',
    fontSize: '13px',
  },
};

export default PushOptions;
